import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, useEffect, useRef, useMemo, useCallback, useDeferredValue } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchChannels,
  createChannel,
  addChannelMembers,
  deleteChannel,
  restoreChannel,
  ensureDynamicChannels,
  fetchMessages,
  deleteMessage,
  updateMessage,
  sendMessage,
  createDmChannel,
  uploadChatAttachment,
  resolveAttachmentUrl,
  markChannelRead,
  fetchChannelReadReceipts,
  type ChatAttachment,
  type ChannelReadReceipt,
} from "@/lib/api/messages";
import { fetchPublicDirectory } from "@/lib/api/users";
import { fetchBatchesAll, updateTeamBatch } from "@/lib/api/teams";
import { useAuth } from "@/hooks/use-auth";
import { canPostInChannel } from "@/lib/rbac";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  MessagesSquare,
  Send,
  Hash,
  Megaphone,
  MessageCircle,
  Reply,
  Plus,
  Settings,
  Trash2,
  Users,
  Pencil,
  AlertCircle,
  Lock,
  Search,
  X,
  Paperclip,
  FileText,
  Download,
  Check,
  CheckCheck,
  ArrowDown,
  ChevronLeft,
  Info,
  ClipboardList,
  Loader2,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { bidiText } from "@/lib/utils";
import { linkifyText } from "@/lib/linkify";

// Polling cadence — the backend has no realtime socket, so we keep the feed
// fresh by re-fetching on an interval and whenever the tab regains focus.
const MESSAGES_POLL_MS = 4000;
const REPLIES_POLL_MS = 8000;
const CHANNELS_POLL_MS = 15000;

// Consecutive messages from the same author within this window are grouped
// together (no repeated avatar/name), matching common chat UI conventions.
const GROUP_WINDOW_MS = 5 * 60 * 1000;

// The composer auto-grows with its content up to this height (~6 lines at
// leading-6), after which it scrolls internally instead of eating the feed.
const COMPOSER_MAX_HEIGHT_PX = 144;

// How long an author may edit their own message, measured from when it was sent. MUST match
// MessageService.EditWindow — the server enforces it and rejects a late edit with a 400; this
// constant only decides whether the Edit button is offered.
const MESSAGE_EDIT_WINDOW_MS = 30 * 60 * 1000;

interface MessagesSearch {
  dmUserId?: string;
}

export const Route = createFileRoute("/_authenticated/messages")({
  validateSearch: (search: Record<string, unknown>): MessagesSearch => {
    return {
      dmUserId: search.dmUserId as string | undefined,
    };
  },
  component: MessagesPage,
});

type Channel = {
  id: string;
  name: string;
  type: string;
  is_read_only: boolean;
  description: string | null;
  last_message_at?: string | null;
  project?: string | null;
  batch?: string | null;
  /** Set only on the two per-batch channels. */
  batch_id?: string | null;
  team_leader_id?: string | null;
  /** Real Channel row id — `id` above is a logical handle ("general", or a group id for teams). */
  channel_id?: string | null;
  /** Server-side unread count for this user. Replaces the old per-browser localStorage read map. */
  unread_count?: number;
  /** This user's own read cursor for the channel; null if they've never opened it. */
  last_read_at?: string | null;
  /**
   * Set only on a GROUP's announcement feed — what tells it apart from the system-wide announcement
   * channel, which carries no group. Not the channel's own id: `id` is the channel row here.
   */
  group_id?: string | null;
  /** Set only on a COURSE announcement channel. */
  course_id?: string | null;
  /**
   * The batch a GROUP's channels (discussion + announcements) belong to, inherited from the group.
   * Distinct from `batch_id`, which marks a per-batch channel: this one carries none of that
   * channel's sidebar placement or posting rights, it only says where the group sits. Null until an
   * admin assigns the group to a batch.
   */
  group_batch_id?: string | null;
  group_batch_name?: string | null;
  /**
   * Archived — hidden from every non-admin listing and refusing new messages, with its history
   * intact. Only ever true in an admin's "show archived" listing; see deleteChannel for why this is
   * what deletion means here.
   */
  is_archived?: boolean;
  /**
   * DM only: the other participant is one of this user's own team leaders. Server-computed, and the
   * reason this conversation exists in the list at all before anybody has written in it.
   */
  is_team_leader_dm?: boolean;
};
type Message = {
  id: string;
  content: string;
  author_id: string;
  created_at: string;
  edited_at?: string | null;
  parent_message_id: string | null;
  author?: { full_name: string | null; email?: string | null } | null;
  lockReplies?: boolean;
  /** Mandatory-acknowledgment announcement — recipients must explicitly confirm it. */
  requiresAcknowledgment?: boolean;
  attachment_url?: string | null;
  attachment_file_name?: string | null;
  attachment_content_type?: string | null;
  attachment_size_bytes?: number | null;
  /**
   * An assignment shared into the chat. Resolved per viewer by the server: when
   * `is_available_to_viewer` is false the remaining fields are deliberately empty, because this
   * reader is not entitled to the assignment's title.
   */
  shared_assignment?: {
    id: string;
    is_available_to_viewer: boolean;
    title: string;
    course_name: string;
    due_date?: string | null;
    points_possible: number;
    is_published: boolean;
  } | null;
};

function isImageAttachment(contentType?: string | null) {
  return !!contentType && contentType.startsWith("image/");
}

function formatFileSize(bytes?: number | null) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * An assignment shared into the chat, as a card the recipient can open.
 *
 * The unavailable branch is not an error state — an announcement feed outlives the enrolment that made
 * it relevant, and an assignment can be unpublished or deleted after being shared. The server tells us
 * whether THIS reader may open it and withholds the title when they may not, so this renders only what
 * it was given and never reconstructs a label from the other fields.
 */
function SharedAssignmentCard({ message }: { message: Message }) {
  const shared = message.shared_assignment;
  if (!shared) return null;

  if (!shared.is_available_to_viewer) {
    return (
      <div className="mt-1.5 max-w-md rounded-lg border border-dashed bg-muted/20 px-3 py-2.5 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <ClipboardList className="h-3.5 w-3.5" aria-hidden />
          An assignment was shared here, but it is no longer available to you.
        </span>
      </div>
    );
  }

  const due = shared.due_date ? new Date(shared.due_date) : null;
  const dueLabel =
    due && !Number.isNaN(due.getTime()) ? `due ${due.toLocaleDateString()}` : "no deadline";

  return (
    <Link
      to="/assignments/$assignmentId"
      params={{ assignmentId: shared.id }}
      className="mt-1.5 block max-w-md rounded-lg border bg-card px-3 py-2.5 transition-colors hover:border-primary/50 hover:bg-primary/5"
    >
      <span className="flex items-start gap-2.5">
        <ClipboardList className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold">{shared.title}</span>
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
            {shared.course_name} · {dueLabel} · {shared.points_possible} points
          </span>
          {!shared.is_published && (
            <span className="mt-1 inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              Draft
            </span>
          )}
          <span className="mt-1.5 block text-xs font-medium text-primary">Open assignment →</span>
        </span>
      </span>
    </Link>
  );
}

/** Renders a message's attachment, if any: inline image preview or a file chip. */
function MessageAttachment({ message }: { message: Message }) {
  if (!message.attachment_url) return null;

  if (isImageAttachment(message.attachment_content_type)) {
    return (
      <a
        href={message.attachment_url}
        target="_blank"
        rel="noopener noreferrer"
        className="block max-w-[240px] overflow-hidden rounded-xl border"
      >
        <img
          src={message.attachment_url}
          alt={message.attachment_file_name ?? "Attachment"}
          className="max-h-60 w-full object-cover"
          loading="lazy"
        />
      </a>
    );
  }

  return (
    <a
      href={message.attachment_url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 rounded-lg border bg-background/60 px-3 py-2 text-xs max-w-[240px] hover:bg-background transition"
    >
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{message.attachment_file_name ?? "File"}</span>
        {message.attachment_size_bytes ? (
          <span className="block text-[10px] text-muted-foreground">
            {formatFileSize(message.attachment_size_bytes)}
          </span>
        ) : null}
      </span>
      <Download className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
    </a>
  );
}

/**
 * Read state is SERVER-side (ChannelReadState), not localStorage.
 *
 * The old `messages_last_read` localStorage map was per-browser, so a channel read on a laptop still
 * showed unread on a phone — and, more importantly, it could never power "Seen": a sender cannot
 * read the recipient's localStorage. Unread counts now arrive on the channel payload and read marks
 * go to POST /messages/channels/{id}/read.
 */

/** True when `readerCursor` is at or after this message — i.e. the reader has seen it. */
function isSeenBy(messageCreatedAt: string, readerCursor?: string | null): boolean {
  if (!readerCursor) return false;
  const seen = new Date(readerCursor).getTime();
  const sent = new Date(messageCreatedAt).getTime();
  if (Number.isNaN(seen) || Number.isNaN(sent)) return false;
  return seen >= sent;
}

function initials(name?: string | null) {
  const n = (name || "User").trim();
  const parts = n.split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join("") || "U";
}

/** Exact, human-readable local time — e.g. "Jul 12, 2026, 1:04:37 PM". */
function exactTime(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function relativeTime(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return formatDistanceToNow(d, { addSuffix: true });
}

/**
 * "1:04 PM" — the standard chat timestamp. Replaces a relative one ("about 2 hours ago") under
 * every bubble: that was noisy at message density, and it silently went stale because nothing
 * re-rendered it on a timer. The day separators carry the date, and the exact instant is still one
 * hover away via the title attribute.
 */
function clockTime(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** True when two timestamps fall on the same local calendar day. */
function isSameDay(a?: string | null, b?: string | null) {
  if (!a || !b) return false;
  const da = new Date(a);
  const db = new Date(b);
  if (isNaN(da.getTime()) || isNaN(db.getTime())) return false;
  return da.toDateString() === db.toDateString();
}

/** "Today" / "Yesterday" / "Mon, Jul 12" for a day separator. */
function dayLabel(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(d.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
  });
}

function isSameGroup(curr: Message, prev?: Message) {
  if (!prev) return false;
  if (prev.author_id !== curr.author_id) return false;
  const gap = new Date(curr.created_at).getTime() - new Date(prev.created_at).getTime();
  return gap >= 0 && gap < GROUP_WINDOW_MS;
}

/** Visual identity (icon + color chip) for a channel type, shared by the sidebar and header. */
function channelTypeMeta(ch: Pick<Channel, "type">) {
  switch (ch.type) {
    case "announcement":
      return { Icon: Megaphone, chip: "bg-accent/15 text-accent" };
    case "team":
      return { Icon: Users, chip: "bg-secondary/15 text-secondary" };
    case "dm":
      return { Icon: MessageCircle, chip: "bg-primary/15 text-primary" };
    default:
      return { Icon: Hash, chip: "bg-muted text-muted-foreground" };
  }
}

function MessagesPage() {
  const { user, isAdmin, hasRole, roles, isTeamLeader, isStudent } = useAuth();
  const [activeChannelId, setActiveChannelId] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [channelSearch, setChannelSearch] = useState("");
  const qc = useQueryClient();

  // Below `md` there is no room for two columns — a 288px channel list on a 375px phone leaves ~87px
  // of conversation — so the two panes become two views of one column: the channel list, then the
  // conversation with a back arrow. Purely a narrow-screen concern; from `md` up both panes render
  // together and this value is ignored, so it is never out of sync with what a desktop user sees.
  const [mobileView, setMobileView] = useState<"list" | "chat">("list");

  const openChannel = (channelId: string) => {
    setActiveChannelId(channelId);
    setMobileView("chat");
    setReplyTo(null);
    setSelectedMessage(null);
  };

  // Arriving via a "Message" button elsewhere in the app (e.g. ?dmUserId=<studentId>)
  // get-or-creates the DM channel with that user and jumps straight to it.
  const { dmUserId } = useSearch({ from: "/_authenticated/messages" });
  const navigate = useNavigate();

  // The channel a ?dmUserId= jump asked for, held in state and merged into the rendered list until
  // the server list catches up. It cannot live in the react-query cache alone: arriving via the
  // Direct Message button mounts this page, so the channel-list request is already in flight when
  // the DM is created — seeding a cache entry that doesn't exist yet is a no-op, and the response
  // that lands was computed from a snapshot taken before the channel row existed. Either way the
  // render (which looks the active id up in the list) fell back to channels[0] and opened the
  // wrong conversation until the next 15s poll.
  const [pendingDmChannel, setPendingDmChannel] = useState<Channel | null>(null);

  const startDmMutation = useMutation({
    mutationFn: async (targetUserId: string) => createDmChannel(targetUserId),
    onSuccess: (channel) => {
      setPendingDmChannel(channel);
      qc.invalidateQueries({ queryKey: ["channels"] });
      setActiveChannelId(channel.id);
      // Landing here from a "Direct Message" button is a request for that conversation, not for the
      // channel list — on a phone it opens straight into it rather than making the user find the row.
      setMobileView("chat");
      navigate({ to: "/messages", search: {}, replace: true });
    },
    onError: (err: Error) => {
      setPendingDmChannel(null);
      // A 403 body is empty, so the generic message would read "API request failed" — which looks
      // like a broken button rather than the permission rule it actually is.
      const status = (err as Error & { status?: number }).status;
      toast.error(
        status === 403
          ? "You can only message students in one of your groups or courses."
          : `Couldn't start that conversation: ${err.message}`,
      );
      navigate({ to: "/messages", search: {}, replace: true });
    },
  });

  const startDmMutateRef = useRef(startDmMutation.mutate);
  startDmMutateRef.current = startDmMutation.mutate;

  useEffect(() => {
    if (dmUserId) startDmMutateRef.current(dmUserId);
    // Only re-run when the incoming search param itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dmUserId]);

  // Dialog visibility states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);

  // Create Channel Form State
  const [newChanName, setNewChanName] = useState("");
  const [newChanDesc, setNewChanDesc] = useState("");
  const [newChanType, setNewChanType] = useState<"team" | "announcement">("team");
  const [newChanReadOnly, setNewChanReadOnly] = useState(false);
  const [newChanMemberIds, setNewChanMemberIds] = useState<Set<string>>(new Set());
  const [memberSearch, setMemberSearch] = useState("");
  const deferredMemberSearch = useDeferredValue(memberSearch);

  // Initial-members picker for the create-channel dialog: only fetches while the
  // dialog is actually open, and searches the org-wide directory server-side with
  // a small page size instead of downloading every profile up front.
  const { data: memberDirectory, isFetching: isMemberSearchLoading } = useQuery({
    queryKey: ["create-channel-directory", deferredMemberSearch],
    queryFn: () => fetchPublicDirectory(deferredMemberSearch, 1, 20),
    enabled: isCreateDialogOpen && !!user?.id && isAdmin,
  });
  const profiles = memberDirectory?.items ?? [];

  // Create Channel Mutation
  const createChannelMutation = useMutation({
    mutationFn: async ({
      name,
      description,
      type,
      isReadOnly,
      memberIds,
    }: {
      name: string;
      description: string;
      type: "team" | "announcement";
      isReadOnly: boolean;
      memberIds: string[];
    }) => {
      // 1. Create channel
      const chanData = await createChannel({
        name: name.trim(),
        description: description?.trim() || null,
        type,
        created_by: user?.id,
      });

      // 2. Add members
      if (memberIds.length > 0) {
        const memberRows = memberIds.map((userId) => ({
          channel_id: chanData.id,
          user_id: userId,
        }));
        await addChannelMembers(memberRows);
      }
    },
    onSuccess: () => {
      toast.success("Channel created successfully");
      qc.invalidateQueries({ queryKey: ["channels"] });
      setIsCreateDialogOpen(false);
      setNewChanName("");
      setNewChanDesc("");
      setNewChanType("team");
      setNewChanReadOnly(false);
      setNewChanMemberIds(new Set());
      setMemberSearch("");
    },
    onError: (err: Error) => {
      toast.error(`Error creating channel: ${err.message}`);
    },
  });

  // Whether an admin is currently being shown archived channels so they can restore one.
  const [showArchived, setShowArchived] = useState(false);

  // Delete Channel Mutation
  //
  // Takes the channel ROW id, not `activeChannel.id`: that is a logical handle ("general", or a
  // group id for a team channel) and the endpoint keys on the real row. Passing `id` here was why
  // this could never have worked against a real backend even once the stub was replaced.
  const deleteChannelMutation = useMutation({
    mutationFn: async (channelRowId: string) => {
      await deleteChannel(channelRowId);
    },
    onSuccess: () => {
      toast.success("Channel archived. Its messages are kept and it can be restored.");
      qc.invalidateQueries({ queryKey: ["channels"] });
      setIsSettingsDialogOpen(false);
      setActiveChannelId(null);
    },
    onError: (err: Error) => {
      toast.error(`Couldn't archive that channel: ${err.message}`);
    },
  });

  const restoreChannelMutation = useMutation({
    mutationFn: async (channelRowId: string) => {
      await restoreChannel(channelRowId);
    },
    onSuccess: () => {
      toast.success("Channel restored");
      qc.invalidateQueries({ queryKey: ["channels"] });
      setIsSettingsDialogOpen(false);
    },
    onError: (err: Error) => {
      toast.error(`Couldn't restore that channel: ${err.message}`);
    },
  });

  const handleToggleNewChanMember = (userId: string) => {
    const updated = new Set(newChanMemberIds);
    if (updated.has(userId)) {
      updated.delete(userId);
    } else {
      updated.add(userId);
    }
    setNewChanMemberIds(updated);
  };

  const {
    data: serverChannels = [],
    isLoading: channelsLoading,
    isError: channelsError,
  } = useQuery<Channel[]>({
    queryKey: ["channels", user?.id, isAdmin, roles, showArchived],
    queryFn: async () => {
      try {
        await ensureDynamicChannels();
      } catch (e) {
        console.error("ensure_dynamic_channels failed:", e);
      }

      return await fetchChannels(user?.id ?? "", isAdmin && showArchived);
    },
    enabled: !!user?.id,
    refetchInterval: CHANNELS_POLL_MS,
    refetchOnWindowFocus: true,
  });

  // A just-opened DM is appended locally so it is selectable and renderable immediately; it drops
  // out again (below) as soon as the server list carries it.
  const channels = useMemo<Channel[]>(() => {
    if (!pendingDmChannel) return serverChannels;
    return serverChannels.some((c) => c.id === pendingDmChannel.id)
      ? serverChannels
      : [...serverChannels, pendingDmChannel];
  }, [serverChannels, pendingDmChannel]);

  const activeChannel = channels.find((c) => c.id === activeChannelId) ?? channels[0] ?? null;

  // ── Admin: which batch does this channel belong to? ──
  //
  // Only a GROUP's two channels have an answer an admin can change. Everything else is either
  // already anchored to a batch by construction (the per-batch pair), or has no batch at all
  // (the platform-wide pair, course feeds, DMs) — so the control is absent there rather than
  // disabled, because there is nothing to assign.
  //
  // The batch lives on the GROUP, not on the channel row: both of a group's channels move together,
  // which is the behavior you want — an announcement feed that stayed behind in the old batch while
  // its discussion moved would be addressed to people who can no longer read the reply.
  const batchAssignableGroupId = useMemo(() => {
    if (!activeChannel) return null;
    // A team channel's own id IS the group id; a group's announcement feed carries group_id instead
    // (its id is the channel row, since a group id already resolves to the discussion channel).
    if (activeChannel.type === "team") return activeChannel.id;
    return activeChannel.group_id ?? null;
  }, [activeChannel]);

  // ── Admin: jump to the same channel in another batch ──
  //
  // A per-batch channel's batch is fixed, not a field: ChannelConfiguration puts a unique index on
  // (BatchId, Type), and every batch already has its pair auto-created, so there is no free slot to
  // move one into — a reassignment would always collide. What an admin actually wants standing in
  // Batch A's general-discussion is the same conversation in Batch B, which is a navigation.
  //
  // Offered from the platform-wide pair too, not only from a channel that already has a batch: the
  // gear on `general-discussion` is exactly where an admin goes looking for "which batch", and
  // finding nothing there reads as a broken dialog.
  //
  // Which channels count as the batch-scoped counterpart cannot be read off `type`. BOTH
  // platform-wide rows serialize as type "announcement" (see GetChannelsAsync), so matching on it
  // from general-discussion would jump to each batch's ANNOUNCEMENTS feed — the wrong channel, and
  // wrong in a way nobody would notice until they posted in it. The logical id is what separates
  // them, so the counterpart is chosen from that instead.
  const batchSiblingType = useMemo(() => {
    if (!activeChannel) return null;
    if (activeChannel.id === "general") return "batch"; // → each batch's general-discussion
    if (activeChannel.id === "announcements") return "announcement"; // → each batch's announcements
    return activeChannel.batch_id ? activeChannel.type : null;
  }, [activeChannel]);

  // Options are derived from the channel list rather than fetched from /admin/batches so that every
  // entry is guaranteed to resolve to a channel this admin can already open.
  const batchSiblings = useMemo(() => {
    if (!batchSiblingType) return [];
    return channels
      .filter((c) => !!c.batch_id && c.type === batchSiblingType)
      .sort((a, b) => (a.batch ?? "").localeCompare(b.batch ?? ""));
  }, [channels, batchSiblingType]);

  // Nothing to offer when the only destination is the channel already on screen.
  const canSwitchBatch = batchSiblings.some((c) => c.id !== activeChannel?.id);

  const [batchDraftId, setBatchDraftId] = useState<string>("");

  // Reset the draft to the server's answer whenever the dialog opens or the channel changes, so a
  // selection abandoned on one channel can't be carried into another and saved there by accident.
  useEffect(() => {
    setBatchDraftId(activeChannel?.group_batch_id ?? "");
  }, [activeChannel?.id, activeChannel?.group_batch_id, isSettingsDialogOpen]);

  const { data: batchOptions = [], isLoading: batchOptionsLoading } = useQuery<
    { id: string; name: string }[]
  >({
    queryKey: ["batches-all"],
    queryFn: fetchBatchesAll,
    enabled: isSettingsDialogOpen && isAdmin && !!batchAssignableGroupId,
    staleTime: 5 * 60_000,
  });

  const updateChannelBatchMutation = useMutation({
    mutationFn: ({ groupId, batchId }: { groupId: string; batchId: string }) =>
      updateTeamBatch(groupId, batchId),
    onSuccess: () => {
      toast.success("Channel moved to the selected batch");
      // The group roster feeding the admin Teams screens changes with it, so both caches go.
      qc.invalidateQueries({ queryKey: ["channels"] });
      qc.invalidateQueries({ queryKey: ["groups"] });
    },
    onError: (err: Error) => {
      toast.error(`Couldn't change the batch: ${err.message}`);
    },
  });

  useEffect(() => {
    if (pendingDmChannel && serverChannels.some((c) => c.id === pendingDmChannel.id)) {
      setPendingDmChannel(null);
    }
  }, [serverChannels, pendingDmChannel]);

  useEffect(() => {
    if (channels.length === 0) return;
    if (!activeChannelId || !channels.find((c) => c.id === activeChannelId)) {
      setActiveChannelId(channels[0].id);
    }
  }, [channels, activeChannelId]);

  // ── Mark the active channel read, server-side ──
  //
  // Re-runs when the newest message changes as well as on channel switch: a channel you are actively
  // looking at must not accrue an unread badge while it is on screen, and the other side's "Seen"
  // marker should advance as you read rather than only when you first open the conversation.
  const activeChannelLastMessageAt = channels.find(
    (c) => c.id === activeChannelId,
  )?.last_message_at;

  useEffect(() => {
    if (!activeChannelId) return;
    let cancelled = false;
    (async () => {
      try {
        await markChannelRead(activeChannelId);
        if (cancelled) return;
        qc.invalidateQueries({ queryKey: ["channels"] });
        qc.invalidateQueries({ queryKey: ["student-has-unread-announcements"] });
        // The sidebar's nav badge polls on a slow cadence of its own, which would leave it showing a
        // count for the conversation the user is currently reading. Reading is exactly when it
        // should drop, so push it rather than waiting for the poll.
        qc.invalidateQueries({ queryKey: ["messages-unread-count"] });
      } catch {
        // Non-fatal: a failed read mark only means the badge lingers. Never surface a toast for it —
        // this fires on every channel open and on every incoming message.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeChannelId, activeChannelLastMessageAt, qc]);

  /**
   * Other participants' read cursors for the active channel — the source of the "Seen" marker.
   * Refetched on the same cadence as messages, and pushed live over SignalR (see the ChannelRead
   * handler below), so a receipt lands without waiting for the poll.
   */
  const { data: readReceipts = [] } = useQuery({
    queryKey: ["channel-receipts", activeChannelId],
    queryFn: () => fetchChannelReadReceipts(activeChannelId!),
    enabled: !!activeChannelId,
    refetchInterval: MESSAGES_POLL_MS,
  });

  // The furthest any other participant has read. For a DM that is simply the other person; for a
  // group channel it is whoever is most caught up, which is what "Seen" should reflect there too.
  const othersCursor = useMemo(() => {
    let latest: string | null = null;
    for (const r of readReceipts) {
      if (!r.last_read_at) continue;
      if (!latest || new Date(r.last_read_at).getTime() > new Date(latest).getTime()) {
        latest = r.last_read_at;
      }
    }
    return latest;
  }, [readReceipts]);

  const totalUnread = useMemo(
    () =>
      channels.reduce(
        (sum, ch) => sum + (ch.id === activeChannelId ? 0 : (ch.unread_count ?? 0)),
        0,
      ),
    [channels, activeChannelId],
  );

  // Only participants who have actually opened the channel carry a cursor; the rest are "not seen"
  // and are filtered out here so the feed never has to re-check.
  const readers = useMemo(() => readReceipts.filter((r) => !!r.last_read_at), [readReceipts]);

  // A read receipt arriving over the hub refreshes the receipt list for the channel it names, so an
  // outgoing message flips to "Seen" without waiting up to MESSAGES_POLL_MS.
  useEffect(() => {
    const onChannelRead = (event: Event) => {
      const detail = (event as CustomEvent<{ channelId?: string }>).detail;
      if (!detail?.channelId) return;
      qc.invalidateQueries({ queryKey: ["channel-receipts", detail.channelId] });
    };
    window.addEventListener("chat:channel-read", onChannelRead);
    return () => window.removeEventListener("chat:channel-read", onChannelRead);
  }, [qc]);

  const isUnread = (ch: Channel) => {
    if (ch.id === activeChannelId) return false;
    return (ch.unread_count ?? 0) > 0;
  };

  // Who may write here — see canPostInChannel, which mirrors the server's send authorization.
  // A group ("team") channel is a two-way discussion: students in the group post and reply there
  // like anyone else. It used to be gated on being the group's FIRST team leader, which silenced
  // students AND co-leaders — they saw the Reply button (a discussion channel sends
  // lockReplies=false) but clicking it did nothing, because the composer holding the reply draft
  // was replaced by "This channel is read-only for your role".
  // An archived channel accepts nothing from anybody, admins included — CreateMessageAsync refuses
  // it server-side, and only an admin can be looking at one in the first place (via "Show
  // archived"), so without this the one person who can see it is the one shown a composer that
  // would 400.
  const canPost = useMemo(
    () => !activeChannel?.is_archived && canPostInChannel({ isAdmin, isTeamLeader }, activeChannel),
    [activeChannel, isAdmin, isTeamLeader],
  );

  // Moderation rights in the active channel: admins everywhere, plus the team
  // leader of a group channel is the admin/moderator of that group's chat.
  const canModerate = useMemo(() => {
    if (isAdmin) return true;
    return !!activeChannel?.team_leader_id && activeChannel.team_leader_id === user?.id;
  }, [activeChannel, isAdmin, user?.id]);

  const filteredChannels = useMemo(() => {
    const q = channelSearch.trim().toLowerCase();
    if (!q) return channels;
    return channels.filter((ch) => (ch.name ?? "").toLowerCase().includes(q));
  }, [channels, channelSearch]);

  // The platform-wide pair, addressed to everybody at once and owned by no batch.
  const platformChannels = useMemo(
    () => filteredChannels.filter((ch) => ch.id === "general" || ch.id === "announcements"),
    [filteredChannels],
  );
  // DMs are a recency list, not a directory: most recently active conversation first, the way every
  // other chat app orders them. The server returns them in Channel-row order, which is roughly "when
  // you first messaged that person" and goes staler the longer the list gets.
  //
  // With ONE exception, which is the first sort key: a student's own team leader stays pinned to the
  // top. That conversation is now listed before anybody has written in it (the server ensures the
  // channel exists), so on recency alone — it has no timestamp — it would sort dead last, underneath
  // every group chat the student has ever been mentioned in. The whole point is that the student can
  // start it without waiting to be messaged first, which needs it to be the row they see.
  //
  // A DM with no messages yet (just opened from a "Direct Message" button) still has no timestamp, so
  // it sorts below every live conversation and falls back to name order — it is on screen as the
  // active channel anyway, and it climbs the list the moment either side actually says something.
  const dmChannels = useMemo(() => {
    const lastActivity = (ch: Channel) => {
      if (!ch.last_message_at) return 0;
      const t = new Date(ch.last_message_at).getTime();
      return Number.isNaN(t) ? 0 : t;
    };
    const pinned = (ch: Channel) => (ch.is_team_leader_dm ? 1 : 0);
    // filter() already copied the array, so sorting in place can't disturb `channels`.
    return filteredChannels
      .filter((ch) => ch.type === "dm")
      .sort(
        (a, b) =>
          pinned(b) - pinned(a) ||
          lastActivity(b) - lastActivity(a) ||
          (a.name ?? "").localeCompare(b.name ?? ""),
      );
  }, [filteredChannels]);
  const hasTeamLeaderDm = dmChannels.some((ch) => ch.is_team_leader_dm);
  // Defined by SUBTRACTION, not by its own predicate. The old positive filter ended in
  // `!ch.team_leader_id`, so any channel that carried a leader id without being a "team" channel
  // matched no section at all and was dropped from the sidebar entirely — a group's announcement
  // feed, and a team leader's own course announcement channels. Subtracting keeps the sections
  // total: a channel shape nobody anticipated lands here rather than disappearing.
  const bucketed = useMemo(
    () => new Set([...platformChannels, ...dmChannels]),
    [platformChannels, dmChannels],
  );

  // ── Everything else, grouped under the batch it belongs to ──
  //
  // The sidebar used to be organised by channel KIND — one section of announcements, one of team
  // channels — which scattered a single batch's four channels across three headings and forced every
  // row to carry its batch name just to be identifiable. At two batches that is merely noisy; the
  // names then overflow the rail and truncate mid-word ("Traning-2026 general-discuss…"), cutting
  // off the one word that says which of the four a row actually is.
  //
  // Grouping by batch lets the heading carry the batch name once and each row carry only what
  // distinguishes it. A channel's batch comes from one of two fields: batch_id on the per-batch
  // pair, group_batch_id on a group's pair (the field added for the admin batch picker).
  const batchSections = useMemo(() => {
    // Still a SUBTRACTION, for the reason the old comment here gave: a positive filter silently
    // dropped any channel shape it did not anticipate. Nothing can fall out of the sidebar now —
    // a channel either files under a batch or lands in `unbatched`.
    const rest = filteredChannels.filter((ch) => !bucketed.has(ch));

    // Within a batch: the room you talk in, the feed you are told things in, then the same two
    // per group.
    const rank = (ch: Channel) => {
      if (ch.type === "batch") return 0;
      if (ch.batch_id) return 1;
      if (ch.type === "team") return 2;
      return 3;
    };

    const byBatch = new Map<string, { name: string; channels: Channel[] }>();
    const unbatched: Channel[] = [];

    for (const ch of rest) {
      const id = ch.batch_id ?? ch.group_batch_id ?? null;
      const name = ch.batch ?? ch.group_batch_name ?? null;
      if (!id || !name) {
        unbatched.push(ch);
        continue;
      }
      if (!byBatch.has(id)) byBatch.set(id, { name, channels: [] });
      byBatch.get(id)!.channels.push(ch);
    }

    for (const section of byBatch.values()) {
      section.channels.sort(
        (a, b) => rank(a) - rank(b) || (a.name ?? "").localeCompare(b.name ?? ""),
      );
    }

    return {
      batches: [...byBatch.values()].sort((a, b) => a.name.localeCompare(b.name)),
      // Its own section rather than a drop: a group not yet assigned to a batch, and a team
      // leader's course feeds, have no batch to file under and must not vanish because of it.
      unbatched,
    };
  }, [filteredChannels, bucketed]);
  const noSearchResults = channelSearch.trim() !== "" && filteredChannels.length === 0;

  /**
   * What a row says once its section heading already says the batch.
   *
   * The server labels a batch's channels "{Batch} general-discussion" and "{Batch} announcements",
   * which is right for a flat list and wrong inside a section headed by that same batch: the rail is
   * narrow, so the repeated prefix is what gets the room and the distinguishing word is what gets
   * truncated away. Stripping it is presentational only — `ch.name` stays the full label everywhere
   * else, including the channel header and the search filter, so searching "Traning-2026" still
   * finds these rows.
   */
  const channelRowLabel = (ch: Channel) => {
    const batchName = ch.batch ?? ch.group_batch_name ?? null;
    if (!batchName || !ch.name) return ch.name;
    const prefix = `${batchName} `;
    return ch.name.startsWith(prefix) ? ch.name.slice(prefix.length) : ch.name;
  };

  const renderChannelButton = (ch: Channel) => {
    const unread = isUnread(ch);
    const active = activeChannelId === ch.id;
    const isDmRow = ch.type === "dm";
    const { Icon } = channelTypeMeta(ch);
    return (
      <button
        key={ch.id}
        onClick={() => openChannel(ch.id)}
        aria-current={active ? "true" : undefined}
        // py-2.5 on touch: a 30px row is under every touch-target guideline, and these rows sit
        // directly on top of each other so a near miss opens the wrong conversation.
        className={`group/chan flex w-full items-center gap-2 rounded-md px-2 py-2.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:py-1.5 ${
          active
            ? "bg-primary/10 font-semibold text-primary"
            : unread
              ? "font-semibold text-foreground hover:bg-muted"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
        }`}
      >
        {/* A DM is a person, so it gets their initials; a channel is a place, so it gets the "#"
            convention this layout is built around. Same 5x5 slot either way, so names stay aligned. */}
        {isDmRow ? (
          <span
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-primary-foreground"
            style={{ background: "var(--gradient-brand)" }}
            aria-hidden="true"
          >
            {initials(ch.name)}
          </span>
        ) : (
          <span
            className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground/70"
            aria-hidden="true"
          >
            {ch.type === "announcement" || ch.type === "course" ? (
              <Icon className="h-3.5 w-3.5" />
            ) : (
              <Hash className="h-3.5 w-3.5" />
            )}
          </span>
        )}

        <span className="min-w-0 flex-1 truncate">{channelRowLabel(ch) || "Unnamed"}</span>

        {/* Says WHY this conversation is at the top of the list, and that it is there to be used —
            a bare name with no messages under it reads as a chat someone else has to start. */}
        {ch.is_team_leader_dm && (
          <span className="shrink-0 rounded-full bg-secondary/15 px-1.5 text-[9px] font-semibold uppercase tracking-wide text-secondary">
            Team leader
          </span>
        )}

        {ch.is_archived && (
          <span className="shrink-0 rounded-full bg-muted px-1.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
            Archived
          </span>
        )}

        {ch.is_read_only && !unread && (
          <Lock
            className="h-3 w-3 shrink-0 text-muted-foreground/50"
            aria-label="Read-only channel"
          />
        )}

        {/* A COUNT, not a bare dot: "5 unread" and "1 unread" are different decisions and a dot
            could say neither. Capped at 99+ so a long-untouched global channel can't stretch the row. */}
        {unread && (
          <span
            className="shrink-0 rounded-full bg-primary px-1.5 text-center text-[10px] font-bold leading-5 text-primary-foreground tabular-nums"
            aria-label={`${ch.unread_count} unread`}
          >
            {(ch.unread_count ?? 0) > 99 ? "99+" : ch.unread_count}
          </span>
        )}
      </button>
    );
  };

  const sidebarSection = (label: string, list: Channel[]) => {
    if (list.length === 0) return false;
    // Unread rolled up to the heading. Headings are batch names now, and the question a reader has
    // when scanning them is "is anything waiting for me in that batch" — answering it here saves
    // reading every row underneath, and survives the section being scrolled past.
    const sectionUnread = list.reduce(
      (n, ch) => n + (isUnread(ch) ? (ch.unread_count ?? 0) : 0),
      0,
    );
    return (
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 px-2 mb-1 flex items-center gap-1.5">
          {/* min-w-0 + truncate: a batch name is user-typed and can be longer than the rail, and
              without this it wraps the count onto its own line and shoves every row down. */}
          <span className="min-w-0 truncate" title={label}>
            {label}
          </span>
          <span className="shrink-0 font-normal normal-case text-muted-foreground/40">
            {list.length}
          </span>
          {sectionUnread > 0 && (
            <span
              className="ml-auto shrink-0 rounded-full bg-primary px-1.5 text-[9px] font-bold leading-4 text-primary-foreground tabular-nums"
              aria-label={`${sectionUnread} unread in ${label}`}
            >
              {sectionUnread > 99 ? "99+" : sectionUnread}
            </span>
          )}
        </p>
        <div className="space-y-0.5">{list.map(renderChannelButton)}</div>
      </div>
    );
  };

  // Extracted so its POSITION can change without duplicating the markup — see the render below.
  const directMessagesSection = (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 px-2 mb-1">
        Direct Messages
      </p>
      <div className="space-y-0.5">
        {dmChannels.map(renderChannelButton)}
        {dmChannels.length === 0 && (
          <p className="text-xs text-muted-foreground px-2 py-1 italic">
            {isStudent ? "Your team leader will appear here." : "No DMs yet"}
          </p>
        )}
      </div>
    </div>
  );

  // Only when there is actually a leader conversation to lead with: for anyone else this section is a
  // recency list of past chats, which does not belong above the announcements they are here to read.
  const dmFirst = isStudent && hasTeamLeaderDm;

  const headerMeta = activeChannel ? channelTypeMeta(activeChannel) : null;

  return (
    // dvh, not vh, for the mobile height: on iOS Safari `100vh` is the height with the address bar
    // *hidden*, so a vh-based chat column puts the composer under the browser chrome until the user
    // scrolls. The subtraction covers the app header (h-14 staff / h-16 student) plus the shell's
    // p-4; desktop keeps the value it already had.
    <div className="flex h-[calc(100dvh-6rem)] overflow-hidden rounded-xl border bg-card shadow-[var(--shadow-card)] font-sans md:h-[calc(100vh-7rem)]">
      {/* Sidebar — the whole screen on a phone until a channel is picked, a fixed column from md up */}
      <aside
        className={`w-full shrink-0 flex-col border-r bg-sidebar/40 md:flex md:w-72 ${
          mobileView === "chat" ? "hidden" : "flex"
        }`}
      >
        <div className="shrink-0 border-b p-3 space-y-2.5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <MessagesSquare className="h-3.5 w-3.5" /> Messages
              {/* Whole-workspace unread, so a channel buried under a collapsed-looking section list
                  is still discoverable without scanning every row. */}
              {totalUnread > 0 && (
                <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold leading-4 text-primary-foreground tabular-nums">
                  {totalUnread > 99 ? "99+" : totalUnread}
                </span>
              )}
            </p>
            {isAdmin && (
              <span className="flex items-center gap-0.5">
                {/* Without this, archiving is a one-way door: an archived channel is absent from
                    every listing, so there would be nowhere left to press Restore. */}
                <button
                  onClick={() => setShowArchived((v) => !v)}
                  aria-pressed={showArchived}
                  className={`transition p-0.5 rounded hover:bg-muted ${
                    showArchived
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-primary"
                  }`}
                  title={showArchived ? "Hide archived channels" : "Show archived channels"}
                >
                  <Archive className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setIsCreateDialogOpen(true)}
                  className="text-muted-foreground hover:text-primary transition p-0.5 rounded hover:bg-muted"
                  title="Create Channel"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={channelSearch}
              onChange={(e) => setChannelSearch(e.target.value)}
              placeholder="Search channels…"
              // 16px on mobile: iOS Safari zooms the whole page in on any focused field under 16px,
              // and the user then has to pinch back out to see the list they were searching.
              className="h-9 pl-8 pr-7 text-base md:h-8 md:text-xs"
            />
            {channelSearch && (
              <button
                onClick={() => setChannelSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                title="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        <ScrollArea className="flex-1">
          {/* pr-4, not a symmetric p-3: the Radix scrollbar is an absolutely-positioned 10px
              overlay on top of the viewport, so a 12px inset on the right left the row badges
              (the "Team leader" pill, the unread count) sitting underneath it. */}
          <div className="p-3 pr-4 space-y-4">
            {channelsLoading ? (
              <div className="space-y-2 px-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-8 rounded-lg bg-muted animate-pulse" />
                ))}
              </div>
            ) : channelsError ? (
              <div className="px-2 py-4 text-xs text-destructive flex items-start gap-2">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Couldn't load channels. Retrying…</span>
              </div>
            ) : noSearchResults ? (
              <p className="text-xs text-muted-foreground px-2 py-4 text-center italic">
                No channels match "{channelSearch}".
              </p>
            ) : (
              <>
                {/*
                  The DM section leads the sidebar for a student whose team leader is in it. Pinning
                  that row to the top of its own section is not enough on a phone: four announcement
                  sections above it push it off the first screen, and a conversation you have to scroll
                  to find is one you assume you do not have.
                */}
                {dmFirst && directMessagesSection}

                {/* One section per batch, each holding that batch's general-discussion, its
                    announcements, and the channels of every group inside it. */}
                {batchSections.batches.map((b) => (
                  <div key={b.name}>{sidebarSection(b.name, b.channels)}</div>
                ))}

                {sidebarSection("Other Channels", batchSections.unbatched)}

                {/* Last, and labelled for what it is: these reach every batch at once, so they are
                    the ones a reader should be slowest to assume are meant for them. */}
                {sidebarSection("Platform-wide", platformChannels)}

                {!dmFirst && directMessagesSection}

                {channels.length === 0 && (
                  <p className="text-xs text-muted-foreground px-2 py-4 text-center italic">
                    No channels available yet.
                  </p>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </aside>

      {/* Chat column + slide-in details panel */}
      <div
        className={`relative min-w-0 flex-1 flex-col overflow-hidden md:flex ${
          mobileView === "list" ? "hidden" : "flex"
        }`}
      >
        {activeChannel ? (
          <>
            {/* Channel header */}
            <div className="flex shrink-0 items-center justify-between gap-2 border-b bg-card px-2 py-2.5 md:gap-3 md:px-4">
              <div className="flex min-w-0 items-center gap-2 md:gap-2.5">
                {/* The only way back to the channel list on a phone, so it leads the header the way
                    a native chat app's does. Hidden from md up, where the list never went away. */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 text-muted-foreground md:hidden"
                  onClick={() => setMobileView("list")}
                  aria-label="Back to channels"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                {activeChannel.type === "dm" ? (
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-primary-foreground"
                    style={{ background: "var(--gradient-brand)" }}
                    aria-hidden="true"
                  >
                    {initials(activeChannel.name)}
                  </span>
                ) : (
                  headerMeta && (
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                      <headerMeta.Icon className="h-4 w-4" />
                    </span>
                  )
                )}
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-1.5">
                    {/* "#" for a place, a bare name for a person — matching the sidebar, so the
                        header confirms which row you clicked rather than restating it differently. */}
                    {activeChannel.type !== "dm" && (
                      <Hash className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                    )}
                    <h2 className="truncate text-sm font-semibold text-foreground">
                      {activeChannel.name}
                    </h2>
                    {activeChannel.is_read_only && (
                      <Badge variant="outline" className="h-4 shrink-0 px-1 text-[9px]">
                        Read-only
                      </Badge>
                    )}
                  </div>
                  {activeChannel.description && (
                    <p className="truncate text-xs text-muted-foreground">
                      {activeChannel.description}
                    </p>
                  )}
                </div>
              </div>
              {isAdmin && activeChannel.type !== "dm" && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground shrink-0"
                  onClick={() => setIsSettingsDialogOpen(true)}
                  title="Channel Settings & Members"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              )}
            </div>

            <MessageFeed
              channelId={activeChannel.id}
              currentUserId={user?.id ?? ""}
              isAdmin={canModerate}
              onReply={setReplyTo}
              replyTo={replyTo}
              onSelect={setSelectedMessage}
              selectedId={selectedMessage?.id ?? null}
              othersCursor={othersCursor}
              readers={readers}
              ownCursor={activeChannel.last_read_at ?? null}
              isDm={activeChannel.type === "dm"}
            />

            {canPost ? (
              <MessageInput
                channelId={activeChannel.id}
                channelType={activeChannel.type}
                channelName={activeChannel.name}
                isDm={activeChannel.type === "dm"}
                userId={user?.id ?? ""}
                replyTo={replyTo}
                onSent={() => setReplyTo(null)}
                onCancelReply={() => setReplyTo(null)}
                canLockReplies={isAdmin || activeChannel.team_leader_id === user?.id}
              />
            ) : (
              <div className="border-t px-4 py-3 text-sm text-muted-foreground bg-muted/30 flex items-center gap-2">
                <Lock className="h-3.5 w-3.5" />
                This channel is read-only for your role.
              </div>
            )}
          </>
        ) : channelsLoading ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            <div className="text-center space-y-2">
              <MessagesSquare className="mx-auto h-10 w-10 opacity-30" />
              <p className="text-sm">Select a channel to view messages</p>
            </div>
          </div>
        )}

        {/* Details panel — slides in over the chat when a message is selected,
            instead of permanently reserving a third column. */}
        {/* Full-width on a phone: the "Details" action is visible on touch devices (the row toolbar
            is unconditionally shown where there is no hover), so hiding the panel below lg made that
            button do nothing at all. A 320px drawer over a 375px screen isn't a drawer anyway. */}
        <div
          className={`absolute inset-y-0 right-0 w-full border-l bg-card shadow-2xl transition-transform duration-200 ease-out sm:w-80 sm:max-w-[85%] ${
            selectedMessage ? "translate-x-0" : "pointer-events-none translate-x-full"
          }`}
        >
          {selectedMessage && activeChannel && (
            <MessageDetailsPanel
              message={selectedMessage}
              channel={activeChannel}
              currentUserId={user?.id ?? ""}
              onClose={() => setSelectedMessage(null)}
            />
          )}
        </div>
      </div>

      {/* Create Channel Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) setMemberSearch("");
        }}
      >
        <DialogContent className="max-w-md max-h-[85vh] flex flex-col p-6 bg-card border rounded-xl shadow-lg">
          <DialogHeader>
            <DialogTitle>Create Channel</DialogTitle>
            <DialogDescription>Create a new channel and select initial members.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 flex-1 overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <Label htmlFor="chanName">Channel Name</Label>
              <Input
                id="chanName"
                value={newChanName}
                onChange={(e) => setNewChanName(e.target.value.toLowerCase().replace(/\s+/g, "-"))}
                placeholder="e.g. general-discussion"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="chanDesc">Description</Label>
              <Textarea
                id="chanDesc"
                value={newChanDesc}
                onChange={(e) => setNewChanDesc(e.target.value)}
                placeholder="What is this channel about?"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="chanType">Channel Type</Label>
                <select
                  id="chanType"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                  value={newChanType}
                  onChange={(e) => setNewChanType(e.target.value as "team" | "announcement")}
                >
                  <option value="team">Team Channel</option>
                  <option value="announcement">Announcement</option>
                </select>
              </div>

              <div className="flex items-center gap-2 pt-6">
                <Checkbox
                  id="chanReadOnly"
                  checked={newChanReadOnly}
                  onCheckedChange={(checked) => setNewChanReadOnly(!!checked)}
                />
                <Label
                  htmlFor="chanReadOnly"
                  className="text-xs font-normal cursor-pointer select-none"
                >
                  Read Only Channel
                </Label>
              </div>
            </div>

            <div className="space-y-2 border-t pt-4">
              <Label className="text-sm font-semibold">Select Members</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder="Search users by name…"
                  className="h-8 pl-8 text-xs"
                />
              </div>
              <div className="rounded-md border divide-y max-h-48 overflow-y-auto bg-background">
                {isMemberSearchLoading ? (
                  <p className="p-3 text-xs text-muted-foreground">Searching…</p>
                ) : profiles.length === 0 ? (
                  <p className="p-3 text-xs text-muted-foreground">No users found</p>
                ) : (
                  profiles.map((profile: any) => (
                    <div key={profile.id} className="flex items-center justify-between p-2">
                      <div className="flex flex-col">
                        <span className="text-xs font-medium text-foreground">
                          {profile.full_name || profile.email || "No name"}
                        </span>
                        {profile.email && (
                          <span className="text-[10px] text-muted-foreground">{profile.email}</span>
                        )}
                      </div>
                      <Checkbox
                        checked={newChanMemberIds.has(profile.id)}
                        onCheckedChange={() => handleToggleNewChanMember(profile.id)}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4 gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDialogOpen(false);
                setMemberSearch("");
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!newChanName.trim() || createChannelMutation.isPending}
              onClick={() =>
                createChannelMutation.mutate({
                  name: newChanName.trim(),
                  description: newChanDesc.trim(),
                  type: newChanType,
                  isReadOnly: newChanReadOnly,
                  memberIds: Array.from(newChanMemberIds),
                })
              }
            >
              {createChannelMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Channel / Member Settings Dialog */}
      <Dialog open={isSettingsDialogOpen} onOpenChange={setIsSettingsDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] flex flex-col p-6 bg-card border rounded-xl shadow-lg">
          <DialogHeader>
            <DialogTitle>Channel Settings</DialogTitle>
            <DialogDescription>
              Details and configuration for "{activeChannel?.name}". Membership is managed with the
              channel's group under Admin → Teams.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 flex-1 overflow-y-auto pr-1">
            <div className="space-y-1.5 border-b pb-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                About Channel
              </p>
              <div className="space-y-1 text-xs text-foreground">
                <p>
                  <span className="font-semibold">Type:</span> {activeChannel?.type}
                </p>
                {activeChannel?.description && (
                  <p>
                    <span className="font-semibold">Description:</span> {activeChannel.description}
                  </p>
                )}
                <p>
                  <span className="font-semibold">Permissions:</span>{" "}
                  {activeChannel?.is_read_only ? "Read Only for non-admins" : "Standard Post"}
                </p>
                {/* Shown for every channel that has an answer, including the per-batch pair, whose
                    batch is read-only here — it is what the channel IS, not a setting. */}
                {(activeChannel?.batch || batchAssignableGroupId) && (
                  <p>
                    <span className="font-semibold">Batch:</span>{" "}
                    {activeChannel?.batch ?? activeChannel?.group_batch_name ?? "Not assigned"}
                  </p>
                )}
              </div>
            </div>

            {canSwitchBatch && (
              <div className="space-y-1.5 border-b pb-4">
                <Label
                  htmlFor="chanBatchJump"
                  className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  Go to batch
                </Label>
                <select
                  id="chanBatchJump"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                  value={activeChannel?.batch_id ?? ""}
                  onChange={(e) => {
                    const target = batchSiblings.find((c) => c.batch_id === e.target.value);
                    if (!target) return;
                    openChannel(target.id);
                    setIsSettingsDialogOpen(false);
                  }}
                >
                  {/* The platform-wide pair belongs to no batch, so it opens on a placeholder
                      rather than on some batch it would appear to already be in. */}
                  {!activeChannel?.batch_id && (
                    <option value="" disabled>
                      Select a batch…
                    </option>
                  )}
                  {batchSiblings.map((c) => (
                    <option key={c.batch_id} value={c.batch_id ?? ""}>
                      {c.batch}
                      {c.batch_id === activeChannel?.batch_id ? "  (current)" : ""}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-muted-foreground">
                  {activeChannel?.batch_id
                    ? "Opens the same channel in that batch."
                    : "This channel is platform-wide. Pick a batch to open that batch's own copy."}{" "}
                  Nothing moves — each batch keeps its own messages.
                </p>
              </div>
            )}

            {batchAssignableGroupId && (
              <div className="space-y-1.5 border-b pb-4">
                <Label
                  htmlFor="chanBatch"
                  className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  Batch
                </Label>
                <select
                  id="chanBatch"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground disabled:opacity-60"
                  value={batchDraftId}
                  disabled={batchOptionsLoading || updateChannelBatchMutation.isPending}
                  onChange={(e) => setBatchDraftId(e.target.value)}
                >
                  {/* A placeholder rather than a real choice: the backend's
                      GroupService.UpdateTeamBatchAsync ignores a null batch, so offering "none"
                      would look like it worked and change nothing. */}
                  <option value="" disabled>
                    {batchOptionsLoading ? "Loading batches…" : "Select a batch…"}
                  </option>
                  {batchOptions.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-muted-foreground">
                  Moves this channel and its announcement feed under the selected batch. Members of
                  the group go with it.
                </p>
                <Button
                  size="sm"
                  className="w-full"
                  disabled={
                    !batchDraftId ||
                    batchDraftId === (activeChannel?.group_batch_id ?? "") ||
                    updateChannelBatchMutation.isPending
                  }
                  onClick={() =>
                    updateChannelBatchMutation.mutate({
                      groupId: batchAssignableGroupId,
                      batchId: batchDraftId,
                    })
                  }
                >
                  {updateChannelBatchMutation.isPending ? "Saving…" : "Save Batch"}
                </Button>
              </div>
            )}

            <div className="border-t pt-4">
              {activeChannel?.is_archived ? (
                <Button
                  className="w-full flex items-center justify-center gap-2"
                  disabled={restoreChannelMutation.isPending || !activeChannel.channel_id}
                  onClick={() => restoreChannelMutation.mutate(activeChannel.channel_id!)}
                >
                  <ArchiveRestore className="h-4 w-4" />
                  {restoreChannelMutation.isPending ? "Restoring…" : "Restore Channel"}
                </Button>
              ) : (
                <>
                  <Button
                    variant="destructive"
                    className="w-full flex items-center justify-center gap-2"
                    // A DM is the two participants' conversation, not a room an admin administers;
                    // the server refuses it, so the button is not offered either.
                    disabled={
                      deleteChannelMutation.isPending ||
                      !activeChannel?.channel_id ||
                      activeChannel.type === "dm"
                    }
                    onClick={() => {
                      if (
                        confirm(
                          `Archive "${activeChannel?.name}"?

It disappears from everyone's channel list and stops accepting messages. Its history is kept, and you can restore it from "Show archived".`,
                        )
                      ) {
                        deleteChannelMutation.mutate(activeChannel!.channel_id!);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" /> Delete Channel
                  </Button>
                  {/* Says what the button does BEFORE it is pressed. The old copy promised that
                      "all message history will be permanently lost", which was never true and is
                      not what an admin retiring a leftover channel wants to be told. */}
                  <p className="mt-1.5 text-[11px] text-muted-foreground">
                    Hides the channel from everyone and stops new messages. Nothing is erased —
                    restore it any time.
                  </p>
                </>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setIsSettingsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MessageDetailsPanel({
  message,
  channel,
  currentUserId,
  onClose,
}: {
  message: Message;
  channel: Channel;
  currentUserId: string;
  onClose: () => void;
}) {
  const isMine = message.author_id === currentUserId;
  const name = message.author?.full_name || message.author?.email || "User";
  const email = message.author?.email;

  return (
    <div className="flex flex-1 flex-col p-4 gap-4 overflow-y-auto h-full">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60">
          Message Details
        </p>
        {/* Bigger target below md: there the panel covers the whole screen, so this X is the only
            way back to the conversation. */}
        <button
          onClick={onClose}
          className="rounded p-2 text-muted-foreground hover:bg-muted hover:text-foreground md:p-1"
          title="Close"
          aria-label="Close message details"
        >
          <X className="h-4 w-4 md:h-3.5 md:w-3.5" />
        </button>
      </div>

      {/* Sender identity */}
      <div className="flex items-center gap-3">
        <div
          className="h-11 w-11 shrink-0 rounded-full flex items-center justify-center text-sm font-bold text-primary-foreground"
          style={{ background: "var(--gradient-brand)" }}
        >
          {initials(name)}
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-foreground truncate">
            {name}
            {isMine && (
              <span className="ml-1 text-[10px] font-normal text-muted-foreground">(You)</span>
            )}
          </p>
          {email ? (
            <a
              href={`mailto:${email}`}
              className="text-xs text-primary hover:underline truncate block"
            >
              {email}
            </a>
          ) : (
            <p className="text-xs text-muted-foreground italic">No email on file</p>
          )}
        </div>
      </div>

      {/* Metadata */}
      <dl className="space-y-2.5 text-xs rounded-lg border bg-muted/30 p-3">
        <div>
          <dt className="font-medium text-muted-foreground">Sent</dt>
          <dd className="text-foreground">{exactTime(message.created_at)}</dd>
          <dd className="text-muted-foreground">{relativeTime(message.created_at)}</dd>
        </div>
        {message.edited_at && (
          <div>
            <dt className="font-medium text-muted-foreground">Edited</dt>
            <dd className="text-foreground">{exactTime(message.edited_at)}</dd>
          </div>
        )}
        <div>
          <dt className="font-medium text-muted-foreground">Channel</dt>
          <dd className="text-foreground">{channel.name}</dd>
        </div>
        {message.parent_message_id && (
          <div>
            <dt className="font-medium text-muted-foreground">Type</dt>
            <dd className="text-foreground">Reply</dd>
          </div>
        )}
      </dl>

      {/* Content preview */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 mb-1.5">
          Content
        </p>
        <div
          {...bidiText}
          className="rounded-lg bg-muted/50 border p-3 text-sm text-foreground whitespace-pre-wrap break-words max-h-60 overflow-y-auto"
        >
          {linkifyText(message.content)}
        </div>
      </div>
    </div>
  );
}

function ReplyItem({
  reply,
  currentUserId,
  isAdmin,
  onEdit,
  onDelete,
  isPending,
}: {
  reply: Message;
  currentUserId: string;
  isAdmin: boolean;
  onEdit: (id: string, content: string) => void;
  onDelete: (id: string) => void;
  isPending: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(reply.content);
  const replyIsMine = reply.author_id === currentUserId;

  // Same window, same reasoning as the top-level row — see MESSAGE_EDIT_WINDOW_MS. A moderator's
  // exemption is not applied here because this control is only ever shown for the author's own reply.
  const replyEditableUntil = new Date(reply.created_at).getTime() + MESSAGE_EDIT_WINDOW_MS;
  const replyWithinEditWindow = Number.isNaN(replyEditableUntil) || Date.now() < replyEditableUntil;

  return (
    <div
      className={`text-sm flex flex-col group/reply ${replyIsMine ? "items-end" : "items-start"}`}
    >
      {!replyIsMine && (reply.author?.full_name || reply.author?.email) && (
        <span className="text-[9px] text-muted-foreground mb-0.5 ml-1">
          {reply.author.full_name || reply.author.email}
        </span>
      )}
      {isEditing ? (
        <div className="flex flex-col gap-2 mt-1 min-w-[200px] bg-muted/50 p-2 rounded-xl border text-foreground">
          <Textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            {...bidiText}
            className="text-xs min-h-[40px] bg-background text-foreground"
          />
          <div className="flex gap-1.5 justify-end">
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-[10px] px-2 py-0"
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-6 text-[10px] px-2 py-0"
              onClick={() => {
                if (editText.trim()) {
                  onEdit(reply.id, editText.trim());
                  setIsEditing(false);
                }
              }}
              disabled={isPending}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {reply.attachment_url && <MessageAttachment message={reply} />}
          <SharedAssignmentCard message={reply} />
          {/* No bubble: a reply hangs under a flat row, so a bubble here would be the only one on
              screen. Left border + author name carries the same "who said this" signal. */}
          {reply.content && (
            <div
              {...bidiText}
              className="select-text whitespace-pre-wrap break-words text-xs leading-relaxed text-foreground"
            >
              {linkifyText(reply.content)}
            </div>
          )}
        </div>
      )}
      <div className="mt-0.5 flex items-center gap-1.5 px-1">
        <span
          className="cursor-default text-[9px] text-muted-foreground tabular-nums"
          title={exactTime(reply.created_at)}
        >
          {clockTime(reply.created_at)}
        </span>
        {/* Replies post through the same endpoint as top-level messages, so they carry the same
            edit window — offering the button here would just produce a 400. */}
        {!isEditing && replyIsMine && replyWithinEditWindow && (
          <button
            onClick={() => {
              setEditText(reply.content);
              setIsEditing(true);
            }}
            className="invisible opacity-0 transition group-hover/reply:visible group-hover/reply:opacity-100 focus-visible:visible focus-visible:opacity-100 [@media(pointer:coarse)]:visible [@media(pointer:coarse)]:opacity-100 text-[9px] text-primary"
          >
            Edit
          </button>
        )}
        {!isEditing && (replyIsMine || isAdmin) && (
          <button
            onClick={() => onDelete(reply.id)}
            className="invisible opacity-0 transition group-hover/reply:visible group-hover/reply:opacity-100 focus-visible:visible focus-visible:opacity-100 [@media(pointer:coarse)]:visible [@media(pointer:coarse)]:opacity-100 text-[9px] text-destructive"
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

function MessageFeed({
  channelId,
  currentUserId,
  isAdmin,
  onReply,
  replyTo,
  onSelect,
  selectedId,
  othersCursor,
  readers,
  ownCursor,
  isDm,
}: {
  channelId: string;
  currentUserId: string;
  isAdmin: boolean;
  onReply: (m: Message) => void;
  replyTo: Message | null;
  onSelect: (m: Message) => void;
  selectedId: string | null;
  /** Furthest point any OTHER participant has read to, or null if nobody has. */
  othersCursor: string | null;
  /** Every other participant who has opened the channel — who "Seen by" names in a group channel. */
  readers: ChannelReadReceipt[];
  /** The caller's OWN cursor as it stood when the channel was opened, for the "New" divider. */
  ownCursor: string | null;
  isDm: boolean;
}) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const MESSAGES_PAGE_SIZE = 50;

  // Always page 1 (newest-first per the API), polled to keep the feed live.
  const {
    data: latestPage,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["messages", channelId, "latest"],
    queryFn: async () => fetchMessages(channelId, 1, MESSAGES_PAGE_SIZE),
    enabled: !!channelId,
    refetchInterval: MESSAGES_POLL_MS,
    refetchOnWindowFocus: true,
  });

  // Older pages (2, 3, …) are loaded on demand via "Load older messages" and
  // accumulated here (oldest-first) so they can be prepended above the latest page.
  const [olderItems, setOlderItems] = useState<Message[]>([]);
  const [nextOlderPage, setNextOlderPage] = useState(2);
  const [lastOlderHasNext, setLastOlderHasNext] = useState<boolean | null>(null);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const pendingScrollRestore = useRef<number | null>(null);

  // Reset accumulated older pages whenever the active channel changes.
  useEffect(() => {
    setOlderItems([]);
    setNextOlderPage(2);
    setLastOlderHasNext(null);
  }, [channelId]);

  // Once at least one older page has been loaded, whether more remain is tracked
  // from that page's own response; before that, fall back to the latest page's flag.
  const hasMoreOlder =
    lastOlderHasNext !== null ? lastOlderHasNext : (latestPage?.hasNextPage ?? false);

  const loadOlderMessages = async () => {
    if (isLoadingOlder || !hasMoreOlder) return;
    setIsLoadingOlder(true);
    const viewport = scrollAreaRef.current?.querySelector<HTMLDivElement>(
      "[data-radix-scroll-area-viewport]",
    );
    const prevScrollHeight = viewport?.scrollHeight ?? 0;
    try {
      const page = await fetchMessages(channelId, nextOlderPage, MESSAGES_PAGE_SIZE);
      setOlderItems((prev) => [...page.items].reverse().concat(prev));
      setLastOlderHasNext(page.hasNextPage);
      setNextOlderPage((n) => n + 1);
      pendingScrollRestore.current = prevScrollHeight;
    } catch {
      toast.error("Couldn't load older messages.");
    } finally {
      setIsLoadingOlder(false);
    }
  };

  // Restore the scroll offset after older messages are prepended, so the
  // viewport doesn't visually jump to the top when history is loaded.
  useEffect(() => {
    if (pendingScrollRestore.current == null) return;
    const viewport = scrollAreaRef.current?.querySelector<HTMLDivElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight - pendingScrollRestore.current;
    }
    pendingScrollRestore.current = null;
  }, [olderItems]);

  // The backend returns newest-first; reverse the latest page for oldest-to-newest
  // chat rendering, then prepend any manually-loaded older history above it.
  const latestMessages = useMemo(() => [...(latestPage?.items ?? [])].reverse(), [latestPage]);
  const messages = useMemo(() => [...olderItems, ...latestMessages], [olderItems, latestMessages]);

  // ── Scroll behaviour ──
  //
  // Auto-scrolling on EVERY new message yanked the viewport to the bottom while the reader was
  // scrolled up reading history — every 4s poll that landed a message stole their place. Follow the
  // conversation only while they are already near the bottom; otherwise leave them where they are
  // and offer the "Jump to latest" pill below.
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [missedWhileAway, setMissedWhileAway] = useState(0);
  const NEAR_BOTTOM_PX = 120;

  const viewportEl = () =>
    scrollAreaRef.current?.querySelector<HTMLDivElement>("[data-radix-scroll-area-viewport]") ??
    null;

  useEffect(() => {
    const viewport = viewportEl();
    if (!viewport) return;
    const onScroll = () => {
      const distance = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      const near = distance <= NEAR_BOTTOM_PX;
      setIsNearBottom(near);
      if (near) setMissedWhileAway(0);
    };
    onScroll();
    viewport.addEventListener("scroll", onScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", onScroll);
  }, [channelId]);

  const scrollToLatest = useCallback((behavior: ScrollBehavior = "smooth") => {
    bottomRef.current?.scrollIntoView({ behavior });
    setMissedWhileAway(0);
  }, []);

  const newestMessageId = latestMessages[latestMessages.length - 1]?.id ?? null;
  const newestIsMine = latestMessages[latestMessages.length - 1]?.author_id === currentUserId;

  useEffect(() => {
    // Own send always scrolls: you just wrote it, you expect to see it land.
    if (isNearBottom || newestIsMine) {
      scrollToLatest();
    } else {
      setMissedWhileAway((n) => n + 1);
    }
    // Intentionally keyed on the newest message / channel only — re-running when isNearBottom
    // changes would scroll on every wheel tick that crosses the threshold.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newestMessageId, channelId]);

  // A channel switch always lands at the bottom, instantly (no animation across a whole history).
  useEffect(() => {
    setMissedWhileAway(0);
    setIsNearBottom(true);
    const id = requestAnimationFrame(() => scrollToLatest("auto"));
    return () => cancelAnimationFrame(id);
  }, [channelId, scrollToLatest]);

  const topLevelMessages = messages.filter((m) => !m.parent_message_id);

  // The caller's newest own message — the only one that carries a delivery state.
  const lastOwnMessage = useMemo(() => {
    for (let i = topLevelMessages.length - 1; i >= 0; i--) {
      if (topLevelMessages[i].author_id === currentUserId) return topLevelMessages[i];
    }
    return null;
  }, [topLevelMessages, currentUserId]);

  /** Who has read the caller's newest own message, most recently caught up first. */
  const seenByReaders = useMemo(
    () =>
      lastOwnMessage
        ? readers.filter((r) => isSeenBy(lastOwnMessage.created_at, r.last_read_at))
        : [],
    [lastOwnMessage, readers],
  );

  /**
   * Sent/Seen for the caller's newest own message. Computed here and handed to that one row rather
   * than rendered as a detached line under the feed, so the state sits on the message it describes.
   * A group channel also carries WHO, since "Seen by 3" in a room of twenty says very little.
   */
  const deliveryState = useMemo(() => {
    if (!lastOwnMessage) return null;
    const seen = isSeenBy(lastOwnMessage.created_at, othersCursor);
    if (!seen) return { seen: false, label: "Sent", readers: [] as ChannelReadReceipt[] };
    return {
      seen: true,
      label: isDm ? "Seen" : `Seen by ${seenByReaders.length}`,
      // DMs have exactly one counterpart, and the header already names them — a roster there would
      // only restate it, so the list is group-channel-only.
      readers: isDm ? [] : seenByReaders,
    };
  }, [lastOwnMessage, othersCursor, isDm, seenByReaders]);

  // The first message the reader had not yet seen WHEN THEY OPENED the channel. Captured once per
  // channel rather than tracked live: the read cursor advances as soon as the channel is opened, so
  // a live value would delete the divider a moment after showing it.
  const openedCursorRef = useRef<string | null>(null);
  const openedChannelRef = useRef<string | null>(null);
  if (openedChannelRef.current !== channelId) {
    openedChannelRef.current = channelId;
    openedCursorRef.current = ownCursor ?? null;
  }
  const firstUnreadId = useMemo(() => {
    const cursor = openedCursorRef.current;
    if (!cursor) return null;
    const first = topLevelMessages.find(
      (m) => m.author_id !== currentUserId && !isSeenBy(m.created_at, cursor),
    );
    return first?.id ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topLevelMessages, currentUserId, channelId]);

  if (isLoading) {
    return (
      <ScrollArea className="flex-1 px-4 py-3">
        <div className="space-y-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={`flex items-end gap-2 ${i % 2 ? "justify-end" : ""}`}>
              {i % 2 === 0 && <div className="h-7 w-7 rounded-full bg-muted animate-pulse" />}
              <div
                className="h-10 rounded-2xl bg-muted animate-pulse"
                style={{ width: `${140 + ((i * 37) % 120)}px` }}
              />
            </div>
          ))}
        </div>
      </ScrollArea>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-3 text-muted-foreground">
          <AlertCircle className="mx-auto h-8 w-8 text-destructive/70" />
          <p className="text-sm">Couldn't load messages for this channel.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (topLevelMessages.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-2 text-muted-foreground">
          <MessagesSquare className="mx-auto h-9 w-9 opacity-30" />
          <p className="text-sm">No messages yet.</p>
          <p className="text-xs opacity-70">Be the first to say something.</p>
        </div>
      </div>
    );
  }

  if (topLevelMessages.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center px-6">
        <div className="text-center text-muted-foreground">
          <MessagesSquare className="mx-auto h-9 w-9 opacity-25" />
          <p className="mt-3 text-sm font-medium text-foreground">No messages yet</p>
          <p className="mt-1 text-xs">Send the first one to start this conversation.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-1 min-h-0 flex-col">
      {/* Tighter side padding on a phone — 32px of gutter is a whole word of message width. */}
      <ScrollArea ref={scrollAreaRef} className="flex-1 px-2 py-3 md:px-4">
        <div>
          {hasMoreOlder && (
            <div className="flex justify-center pb-3">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={loadOlderMessages}
                disabled={isLoadingOlder}
              >
                {isLoadingOlder ? "Loading…" : "Load older messages"}
              </Button>
            </div>
          )}
          {topLevelMessages.map((msg, idx) => {
            const prev = topLevelMessages[idx - 1];
            return (
              <div key={msg.id}>
                {/* Day separator. Without one, a conversation with gaps reads as one continuous
                  session and the per-message times ("1:04 PM") are ambiguous across days. */}
                {!isSameDay(msg.created_at, prev?.created_at) && (
                  <div className="flex items-center gap-3 py-3" role="separator">
                    <span className="h-px flex-1 bg-border" />
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {dayLabel(msg.created_at)}
                    </span>
                    <span className="h-px flex-1 bg-border" />
                  </div>
                )}
                {/* "New" divider above the first message the reader hadn't seen when they opened the
                  channel. Frozen at mount so it doesn't jump as the cursor advances underneath. */}
                {firstUnreadId === msg.id && (
                  <div className="flex items-center gap-3 py-2" role="separator">
                    <span className="h-px flex-1 bg-primary/40" />
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                      New
                    </span>
                    <span className="h-px flex-1 bg-primary/40" />
                  </div>
                )}
                <MessageThread
                  message={msg}
                  grouped={isSameGroup(msg, prev)}
                  channelId={channelId}
                  currentUserId={currentUserId}
                  isAdmin={isAdmin}
                  onReply={onReply}
                  onSelect={onSelect}
                  selectedId={selectedId}
                  deliveryState={msg.id === lastOwnMessage?.id ? deliveryState : null}
                />
              </div>
            );
          })}

          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      {/* Jump to latest. Only offered when the reader is actually away from the bottom, and it
        counts what arrived while they were away so they know whether it's worth the trip. */}
      {!isNearBottom && (
        <button
          type="button"
          onClick={() => scrollToLatest()}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 rounded-full border bg-card/95 px-3 py-1.5 text-xs font-medium shadow-lg backdrop-blur transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowDown className="h-3.5 w-3.5" />
          {missedWhileAway > 0
            ? `${missedWhileAway} new message${missedWhileAway === 1 ? "" : "s"}`
            : "Jump to latest"}
        </button>
      )}
    </div>
  );
}

type DeliveryStateValue = {
  seen: boolean;
  label: string;
  /** Populated for group channels only; empty in a DM and while the message is merely "Sent". */
  readers: ChannelReadReceipt[];
};

/**
 * The Sent/Seen marker on the caller's newest message. In a group channel the "Seen by N" half is a
 * button that opens the roster — a count alone doesn't answer the question people actually have
 * ("did the two people I needed read this?"), and a hover tooltip would hide that answer from every
 * touch device.
 */
function DeliveryState({ state }: { state: DeliveryStateValue }) {
  const tone = state.seen ? "text-primary" : "text-muted-foreground";
  const Icon = state.seen ? CheckCheck : Check;
  const body = (
    <>
      <Icon className="h-3.5 w-3.5" />
      {state.label}
    </>
  );

  if (state.readers.length === 0) {
    return <span className={`flex items-center gap-1 text-[11px] ${tone}`}>{body}</span>;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex items-center gap-1 rounded-sm text-[11px] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${tone}`}
          aria-label={`${state.label} — show who has read this message`}
        >
          {body}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-0">
        <p className="border-b px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
          Seen by {state.readers.length}
        </p>
        <div className="max-h-56 overflow-y-auto">
          <ul className="p-1">
            {state.readers.map((r) => (
              <li key={r.user_id} className="flex items-center gap-2 rounded-md px-2 py-1.5">
                <span
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-primary-foreground"
                  style={{ background: "var(--gradient-brand)" }}
                  aria-hidden="true"
                >
                  {initials(r.full_name || r.email)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-medium text-foreground">
                    {r.full_name || r.email || "User"}
                  </span>
                  <span
                    className="block truncate text-[10px] text-muted-foreground"
                    title={exactTime(r.last_read_at)}
                  >
                    {relativeTime(r.last_read_at)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MessageThread({
  message,
  grouped,
  channelId,
  currentUserId,
  isAdmin,
  onReply,
  onSelect,
  selectedId,
  deliveryState,
}: {
  message: Message;
  grouped: boolean;
  channelId: string;
  currentUserId: string;
  isAdmin: boolean;
  onReply: (m: Message) => void;
  onSelect: (m: Message) => void;
  selectedId: string | null;
  /** Set ONLY on the caller's newest own message — the one row that shows Sent/Seen. */
  deliveryState?: DeliveryStateValue | null;
}) {
  const [showReplies, setShowReplies] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message.content);
  const qc = useQueryClient();

  // A reply thread is expected to stay small; fetch a single generous page rather
  // than building out "load older" infra for it. The API still returns newest-first,
  // so reverse for oldest-to-newest display.
  const { data: repliesPage } = useQuery({
    queryKey: ["replies", message.id],
    queryFn: async () => fetchMessages(message.id, 1, 100),
    enabled: showReplies,
    refetchInterval: showReplies ? REPLIES_POLL_MS : false,
    refetchOnWindowFocus: true,
  });
  const replies: Message[] = useMemo(
    () => [...(repliesPage?.items ?? [])].reverse(),
    [repliesPage],
  );

  const deleteMessageMutation = useMutation({
    mutationFn: async (messageId: string) => {
      await deleteMessage(messageId);
    },
    onSuccess: () => {
      toast.success("Message deleted");
      qc.invalidateQueries({ queryKey: ["messages", channelId] });
      qc.invalidateQueries({ queryKey: ["replies", message.id] });
    },
    onError: (err: Error) => toast.error("Failed to delete message: " + err.message),
  });

  const editMessage = useMutation({
    mutationFn: async ({ messageId, content }: { messageId: string; content: string }) => {
      await updateMessage(messageId, content);
    },
    onSuccess: () => {
      toast.success("Message updated");
      qc.invalidateQueries({ queryKey: ["messages", channelId] });
      qc.invalidateQueries({ queryKey: ["replies", message.id] });
    },
    onError: (err: Error) => toast.error("Failed to update message: " + err.message),
  });

  const isMine = message.author_id === currentUserId;

  const handleDelete = (id: string) => {
    if (confirm("Are you sure you want to delete this message?")) {
      deleteMessageMutation.mutate(id);
    }
  };

  const handleEdit = (id: string, content: string) => {
    editMessage.mutate({ messageId: id, content });
  };

  const authorName = isMine ? "You" : message.author?.full_name || message.author?.email || "User";
  const isSelected = selectedId === message.id;
  const canReply = !message.lockReplies || isAdmin || isMine;

  // The author's own edit window, mirroring MessageService.EditWindow. The server is the authority —
  // this only decides whether to OFFER the button, so a clock skew can at worst produce a 400 the
  // composer surfaces, never an edit the server would have refused.
  //
  // Re-derived on each render rather than held in state: a row re-renders on every poll, so the
  // button disappears within one poll of the window closing without a timer per message.
  const editableUntil = new Date(message.created_at).getTime() + MESSAGE_EDIT_WINDOW_MS;
  const withinEditWindow = Number.isNaN(editableUntil) || Date.now() < editableUntil;
  const canEdit = isMine && (isAdmin || withinEditWindow);

  // Revealed on hover OR keyboard focus, and unconditionally where there is no hover to rely on.
  // `invisible` (not opacity-0) is deliberate: it takes the buttons out of the tab order while
  // hidden, so a keyboard user is not tabbing through controls they cannot see.
  const revealCls =
    "invisible opacity-0 transition group-hover/row:visible group-hover/row:opacity-100 " +
    "group-focus-within/row:visible group-focus-within/row:opacity-100 " +
    "[@media(pointer:coarse)]:visible [@media(pointer:coarse)]:opacity-100";

  const actionBtn =
    "flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors " +
    "hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 " +
    "focus-visible:ring-ring";

  return (
    <div
      className={`group/row relative rounded-lg px-2 transition-colors hover:bg-muted/40 ${
        grouped ? "py-0.5" : "mt-3 pt-1 pb-0.5"
      } ${isSelected ? "bg-primary/5 ring-1 ring-primary/30" : ""}`}
    >
      <div className="flex gap-3">
        {/* Fixed avatar gutter. A grouped message leaves it empty but keeps the width, so every
            line of a run stays aligned — and shows its own timestamp there on hover instead. */}
        <div className="w-9 shrink-0 pt-0.5">
          {grouped ? (
            <span
              className={`${revealCls} block pt-0.5 text-right text-[10px] leading-4 text-muted-foreground tabular-nums`}
              title={exactTime(message.created_at)}
            >
              {clockTime(message.created_at)}
            </span>
          ) : (
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg text-xs font-bold text-primary-foreground"
              style={{ background: "var(--gradient-brand)" }}
              title={message.author?.full_name || message.author?.email || "User"}
            >
              {initials(message.author?.full_name || message.author?.email)}
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          {!grouped && (
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-sm font-semibold text-foreground">{authorName}</span>
              <span
                className="text-[11px] text-muted-foreground tabular-nums"
                title={exactTime(message.created_at)}
              >
                {clockTime(message.created_at)}
              </span>
              {message.edited_at && (
                <span
                  className="text-[10px] text-muted-foreground/70"
                  title={exactTime(message.edited_at)}
                >
                  (edited)
                </span>
              )}
              {message.requiresAcknowledgment && (
                <Badge variant="outline" className="h-4 px-1 text-[9px] font-medium">
                  Acknowledgment required
                </Badge>
              )}
              {/* Delivery state sits ON the row it describes, rather than as a detached line under
                  the feed. Only the caller's newest message carries one — if that one is seen,
                  everything before it is too. */}
              {deliveryState && <DeliveryState state={deliveryState} />}
            </div>
          )}

          {isEditing ? (
            <div className="mt-1 flex flex-col gap-2">
              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                {...bidiText}
                className="min-h-[64px] bg-background text-sm text-foreground"
                autoFocus
              />
              <div className="flex justify-end gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => {
                    if (editText.trim()) {
                      handleEdit(message.id, editText.trim());
                      setIsEditing(false);
                    }
                  }}
                  disabled={editMessage.isPending}
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Plain text, not a control: a message has to be selectable so it can be copied,
                  and a whole paragraph wrapped in a button announced itself to a screen reader as
                  one enormous control. "Details" is an explicit toolbar action instead. */}
              {message.content && (
                <p
                  {...bidiText}
                  className="select-text whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground"
                >
                  {linkifyText(message.content)}
                </p>
              )}
              {message.attachment_url && (
                <div className="mt-1.5 max-w-md">
                  <MessageAttachment message={message} />
                </div>
              )}
              <SharedAssignmentCard message={message} />
              {/* A grouped row has no header to hang the marker on, and the newest message is very
                  often a continuation of your own run — so it gets its own line under the content
                  rather than losing the state entirely. */}
              {grouped && deliveryState && (
                <div className="mt-0.5 flex">
                  <DeliveryState state={deliveryState} />
                </div>
              )}
            </>
          )}

          {/* The reply count doubles as the expander — the count IS the affordance, so there is no
              separate "view replies" control to hunt for. */}
          {!isEditing && (replies.length > 0 || showReplies) && (
            <button
              onClick={() => setShowReplies((v) => !v)}
              className="mt-1 flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
            >
              <MessageCircle className="h-3 w-3" />
              {showReplies
                ? "Hide replies"
                : `${replies.length} ${replies.length === 1 ? "reply" : "replies"}`}
            </button>
          )}
          {!isEditing && replies.length === 0 && !showReplies && canReply && (
            <button
              onClick={() => setShowReplies(true)}
              className={`${revealCls} mt-1 flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground`}
            >
              <MessageCircle className="h-3 w-3" /> View thread
            </button>
          )}

          {showReplies && replies.length > 0 && (
            <div className="mt-2 space-y-2 border-l-2 border-border pl-3">
              {replies.map((r) => (
                <ReplyItem
                  key={r.id}
                  reply={r}
                  currentUserId={currentUserId}
                  isAdmin={isAdmin}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isPending={editMessage.isPending}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Floating action toolbar, anchored to the row's top-right the way every threaded chat does
          it — so it never reflows the message text as it appears. */}
      {!isEditing && (
        <div
          className={`${revealCls} absolute -top-3 right-2 flex items-center gap-0.5 rounded-lg border bg-card p-0.5 shadow-md`}
        >
          {canReply && (
            <button onClick={() => onReply(message)} className={actionBtn} title="Reply">
              <Reply className="h-3.5 w-3.5" />
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => {
                setEditText(message.content);
                setIsEditing(true);
              }}
              className={actionBtn}
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          <button onClick={() => onSelect(message)} className={actionBtn} title="Details">
            <Info className="h-3.5 w-3.5" />
          </button>
          {(isMine || isAdmin) && (
            <button
              onClick={() => handleDelete(message.id)}
              className={`${actionBtn} hover:bg-destructive/10 hover:text-destructive`}
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function MessageInput({
  channelId,
  channelType,
  channelName,
  isDm,
  userId,
  replyTo,
  onSent,
  onCancelReply,
  canLockReplies = false,
}: {
  channelId: string;
  channelType: string;
  /** Names the destination in the placeholder, so a mis-clicked channel is obvious before sending. */
  channelName?: string;
  isDm?: boolean;
  userId: string;
  replyTo: Message | null;
  onSent: () => void;
  onCancelReply: () => void;
  canLockReplies?: boolean;
}) {
  const { isAdmin } = useAuth();
  const [text, setText] = useState("");
  const [requiresAck, setRequiresAck] = useState(false);
  const [lockReplies, setLockReplies] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState<ChatAttachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  // Guards against double-sending. `send.isPending` can't do this on its own:
  // two Enter presses (or clicks) inside the same frame both read the
  // pre-re-render `isPending === false` and each fire their own POST.
  const inFlightRef = useRef(false);
  const qc = useQueryClient();

  // Grow the composer with its content up to ~6 lines, then scroll internally.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
  }, [text]);

  const uploadAttachment = useMutation({
    mutationFn: (file: File) => uploadChatAttachment(file),
    onSuccess: setPendingAttachment,
    onError: (err: Error) => toast.error(err.message || "Failed to upload attachment"),
  });

  const handleFileSelected = (e: any) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File exceeds the 10 MB limit.");
      return;
    }
    uploadAttachment.mutate(file);
  };

  // The payload is snapshotted by `submit` and passed in, so the request is
  // unaffected by anything typed while it is in flight.
  const send = useMutation({
    mutationFn: async ({
      content,
      attachment,
    }: {
      content: string;
      attachment: ChatAttachment | null;
    }) => {
      await sendMessage({
        channel_id: channelId,
        sender_id: userId,
        content,
        parent_message_id: replyTo?.id ?? null,
        requires_acknowledgment: requiresAck,
        lock_replies: lockReplies,
        attachment_url: attachment?.url ?? null,
        attachment_file_name: attachment?.file_name ?? null,
        attachment_content_type: attachment?.content_type ?? null,
        attachment_size_bytes: attachment?.size_bytes ?? null,
      });
    },
    onSuccess: () => {
      setText("");
      setRequiresAck(false);
      setLockReplies(false);
      setPendingAttachment(null);
      onSent();
      qc.invalidateQueries({ queryKey: ["messages", channelId] });
      if (replyTo) qc.invalidateQueries({ queryKey: ["replies", replyTo.id] });
      composerRef.current?.focus();
    },
    // The draft is deliberately left in the box on failure so it isn't lost.
    onError: () => toast.error("Failed to send message"),
    onSettled: () => {
      inFlightRef.current = false;
    },
  });

  /** Single entry point for sending — every trigger (Enter, click) goes through it. */
  const submit = () => {
    if (inFlightRef.current || uploadAttachment.isPending) return;
    const content = text.trim();
    if (!content && !pendingAttachment) return;
    inFlightRef.current = true;
    send.mutate({ content, attachment: pendingAttachment });
  };

  const showOptions = (isAdmin && channelType === "announcement") || (canLockReplies && !replyTo);
  const canSend =
    (!!text.trim() || !!pendingAttachment) && !send.isPending && !uploadAttachment.isPending;

  return (
    <div className="border-t p-3 space-y-2 bg-card">
      {replyTo && (
        <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-1.5 text-xs text-muted-foreground">
          <Reply className="h-3.5 w-3.5 shrink-0" />
          {/* The quoted snippet gets its own bidi context so an Arabic excerpt
              doesn't drag the surrounding label's punctuation around. */}
          <span className="truncate flex-1">
            Replying:{" "}
            <span {...bidiText} className="inline-block max-w-full truncate align-bottom">
              {replyTo.content.slice(0, 60)}
            </span>
          </span>
          <button onClick={onCancelReply} className="text-muted-foreground hover:text-foreground">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {showOptions && (
        <div className="flex flex-wrap items-center gap-4 px-1">
          {isAdmin && channelType === "announcement" && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="requires-ack"
                checked={requiresAck}
                onCheckedChange={(checked) => setRequiresAck(!!checked)}
              />
              <Label
                htmlFor="requires-ack"
                className="text-xs font-normal cursor-pointer select-none text-muted-foreground hover:text-foreground"
              >
                Require pop-up acknowledgment for students
              </Label>
            </div>
          )}
          {canLockReplies && !replyTo && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="lock-replies"
                checked={lockReplies}
                onCheckedChange={(checked) => setLockReplies(!!checked)}
              />
              <Label
                htmlFor="lock-replies"
                className="text-xs font-normal cursor-pointer select-none text-muted-foreground hover:text-foreground"
              >
                Lock replies (Read-only thread)
              </Label>
            </div>
          )}
        </div>
      )}
      {(pendingAttachment || uploadAttachment.isPending) && (
        <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-1.5 text-xs">
          {uploadAttachment.isPending ? (
            <span className="text-muted-foreground">Uploading…</span>
          ) : (
            pendingAttachment && (
              <>
                {isImageAttachment(pendingAttachment.content_type) ? (
                  <img
                    src={resolveAttachmentUrl(pendingAttachment.url) ?? undefined}
                    alt={pendingAttachment.file_name}
                    className="h-8 w-8 rounded object-cover shrink-0"
                  />
                ) : (
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <span className="truncate flex-1 text-muted-foreground">
                  {pendingAttachment.file_name}
                </span>
              </>
            )
          )}
          <button
            onClick={() => setPendingAttachment(null)}
            disabled={uploadAttachment.isPending}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <div className="flex items-end gap-1.5 rounded-xl border bg-background py-1.5 pl-2 pr-1.5 transition focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-ring/30">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip"
          className="hidden"
          onChange={handleFileSelected}
        />
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 rounded-full shrink-0 text-muted-foreground"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadAttachment.isPending || !!pendingAttachment}
          title="Attach a file"
        >
          <Paperclip className="h-4 w-4" />
        </Button>
        <textarea
          ref={composerRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            isDm ? `Message ${channelName ?? ""}…` : `Message #${channelName ?? "channel"}…`
          }
          rows={1}
          {...bidiText}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter inserts a newline. Other modifier combos
            // are left to the browser, and an Enter that merely commits an IME
            // composition is not a submit.
            if (e.key !== "Enter") return;
            if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
            if (e.nativeEvent.isComposing) return;
            e.preventDefault();
            submit();
          }}
          // text-base below md for the same iOS zoom reason as the channel search — and a composer
          // that zooms on every focus is the one field where it hurts most.
          className="min-w-0 flex-1 resize-none bg-transparent px-0 py-1 text-base leading-6 outline-none placeholder:text-muted-foreground md:text-sm"
        />
        <Button
          size="icon"
          className="h-8 w-8 shrink-0 rounded-lg"
          onClick={submit}
          disabled={!canSend}
          title="Send message"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
      {text.trim().length > 0 && (
        <p className="px-1 text-[10px] text-muted-foreground">
          <kbd className="rounded border bg-muted px-1 font-sans">Enter</kbd> to send ·{" "}
          <kbd className="rounded border bg-muted px-1 font-sans">Shift</kbd> +{" "}
          <kbd className="rounded border bg-muted px-1 font-sans">Enter</kbd> for a new line
        </p>
      )}
    </div>
  );
}
